import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Rocket } from "lucide-react";
import AudioButton from "../AudioButton";
import Confetti from "../Confetti";
import { speak, speakPhoneme, speakWord, preload } from "../../services/phonicsAudio";
import { sfx } from "../../services/soundfx";

export default function DigraphGame({ level, onFinish }) {
  const rounds = level.rounds;
  const [idx, setIdx] = useState(0);
  const [solved, setSolved] = useState(false);
  const [mistakes, setMistakes] = useState(0);
  const [wrong, setWrong] = useState(null);
  const [burst, setBurst] = useState(false);
  const round = rounds[idx];

  useEffect(() => {
    preload([
      { text: round.target, kind: "phoneme" },
      { text: round.word, kind: "word" },
    ]);
    const t = setTimeout(() => {
      speakPhoneme(round.target);
      setTimeout(() => speak(`Which two letters make the word ${round.word}?`), 800);
    }, 400);
    return () => clearTimeout(t);
  }, [idx]); // eslint-disable-line

  const pick = (opt) => {
    if (solved) return;
    if (opt === round.target) {
      sfx.correct();
      setSolved(true);
      setBurst(true);
      speakPhoneme(round.target);
      setTimeout(() => speakWord(round.word), 800);
      setTimeout(() => setBurst(false), 1400);
      setTimeout(() => {
        if (idx + 1 >= rounds.length) {
          const stars = mistakes === 0 ? 3 : mistakes <= 2 ? 2 : 1;
          onFinish({ stars, accuracy: Math.max(0, 1 - mistakes / (rounds.length * 2)) });
        } else {
          setIdx((i) => i + 1);
          setSolved(false);
        }
      }, 2200);
    } else {
      sfx.wrong();
      setMistakes((m) => m + 1);
      setWrong(opt);
      speak("Try again!");
      setTimeout(() => setWrong(null), 500);
    }
  };

  return (
    <div className="relative flex flex-col items-center px-4 pt-3 pb-8 min-h-full no-select">
      <Confetti show={burst} count={50} />

      <div className="flex items-center gap-3 bg-white/95 rounded-3xl border-4 border-teal-300 shadow-lg px-4 py-3 mt-2 max-w-lg">
        <AudioButton text={`Which two letters make the word ${round.word}?`} testId="prompt-audio" size={56} />
        <p className="font-display text-lg sm:text-xl font-bold text-teal-900" data-testid="game-prompt">
          Finish the word <span className="text-teal-600 font-black">"{round.word}"</span>
        </p>
      </div>

      {/* Rocket + picture */}
      <motion.div animate={solved ? { y: [0, -14, 0] } : { y: [0, -8, 0] }} transition={{ repeat: Infinity, duration: 2 }} className="my-6 text-7xl" data-testid="digraph-picture">
        {round.emoji}
      </motion.div>

      {/* Word with blank */}
      <div className="flex items-end gap-1 rounded-3xl bg-white border-4 border-teal-200 shadow-[0_8px_0_#5EEAD4] px-6 py-5 mb-8">
        <span className="font-display text-5xl sm:text-6xl font-black text-slate-800">{round.before}</span>
        <div
          data-testid="digraph-blank"
          className={`flex items-center justify-center rounded-xl border-4 ${solved ? "bg-teal-500 border-teal-200 text-white" : "border-dashed border-teal-400 text-transparent"} font-display text-5xl sm:text-6xl font-black`}
          style={{ minWidth: 110, height: 72, padding: "0 8px" }}
        >
          {solved ? round.target : "__"}
        </div>
        <span className="font-display text-5xl sm:text-6xl font-black text-slate-800">{round.after}</span>
      </div>

      {/* Digraph options */}
      <div className="flex flex-wrap items-center justify-center gap-4">
        <AnimatePresence>
          {round.options.map((opt) => (
            <motion.button
              key={opt}
              data-testid={`digraph-option-${opt}`}
              onClick={() => pick(opt)}
              initial={{ scale: 0 }}
              animate={{ scale: 1, x: wrong === opt ? [0, -8, 8, 0] : 0 }}
              whileTap={{ scale: 0.9 }}
              className="pop-btn flex items-center justify-center rounded-2xl bg-teal-500 text-white font-display text-4xl font-black border-4 border-white shadow-[0_6px_0_#0F766E] active:shadow-none"
              style={{ width: 96, height: 80 }}
            >
              {opt}
            </motion.button>
          ))}
        </AnimatePresence>
      </div>

      <div className="flex gap-2 mt-8">
        {rounds.map((_, i) => (
          <div key={i} className={`w-3 h-3 rounded-full ${i < idx ? "bg-green-500" : i === idx ? "bg-teal-400 animate-pulse" : "bg-slate-300"}`} />
        ))}
      </div>
    </div>
  );
}
